/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */
import React from 'react';
import { Redirect } from 'react-router-dom';
import { requestStateConfig } from 'components/layouts/Navigation/RequestStateConfig';
import AccountDetailsPage from 'components/pages/AccountDetails/AccountDetailsPage';
import AccountsPage from 'components/pages/Accounts/AccountsPage';
import BalanceDetailsPage from "components/pages/BalanceDetails/BalanceDetailsPage";
import PerformancePage from 'components/pages/Performance/PerformancePage';
import TaxesPageV2 from '../../pages/Taxes/TaxesPage';
import DashboardPage from 'components/pages/Dashboard/DashboardPage';
import StatementsPage from 'components/pages/Statements/StatementsPage';
import ErrorPage from 'components/shared/ErrorPage/ErrorPage';
import TransactionsPage from 'components/pages/Transactions/TransactionsPage';
import Benefits from 'components/layouts/Preferences/Benefits/Benefits';
import TurboTaxPage from 'components/layouts/Preferences/Benefits/TurboTaxPage';
import ProgramBenefitsPage from 'components/layouts/Preferences/Benefits/ProgramBenefits/ProgramBenefitsPage';
import {
  enhancedPersonalServicesBenefitsUrl,
  personalServicesBenefitsUrl,
  coreServicesBenefitsUrl
} from 'services/Externals';
import {
  balanceDetailsEnabled,
  internalCoreBenefitsPageEnabled,
  internalEnhancedBenefitsPageEnabled,
  internalPersonalBenefitsPageEnabled,
  isBrokerageOrderStatusTablesEnabled,
  oldBenefitsEnabled
} from 'services/FeatureFlags';
import BrokerageErrorPage from 'components/shared/ErrorPage/BrokerageErrorPage';
import WsjErrorPage from 'components/shared/ErrorPage/WsjErrorPage';
import MissingInfoErrorPage from 'components/shared/ErrorPage/MissingInfoErrorPage';
import NoAddressErrorPage from 'components/shared/ErrorPage/NoAddressErrorPage';
import ExcessAccountsPage from 'components/shared/ErrorPage/ExcessAccountsPage';
import TransactionsPageV2 from 'components/pages/Transactions/TransactionsPageV2';

export const routerConfig = [
  {
    path: '/',
    to: '/dashboard',
    index: true,
    exact: true,
    name: 'Dashboard',
    component: DashboardPage,
    external: false,
    requestStates: requestStateConfig['dashboard']
  },
  {
    path: '/login*',
    to: '/dashboard',
    index: true,
    exact: true,
    component: DashboardPage,
    external: false,
    requestStates: requestStateConfig['dashboard']
  },
  {
    path: '/dashboard',
    to: '/dashboard',
    indexSource: true,
    name: 'Dashboard',
    exact: true,
    component: DashboardPage,
    external: false,
    requestStates: requestStateConfig['dashboard']
  },
  {
    path: '/accounts',
    to: '/accounts',
    name: 'Accounts',
    exact: true,
    component: AccountsPage,
    external: false,
    requestStates: requestStateConfig['accounts']
  },
  {
    path: '/account-details',
    to: '/account-details',
    name: 'Account Details',
    exact: true,
    component: AccountDetailsPage,
    external: false,
    requestStates: requestStateConfig['account-details'],
    hide: true
  },
  {
    path: '/transactions',
    to: '/transactions',
    name: 'Activity',
    exact: true,
    component: isBrokerageOrderStatusTablesEnabled()
      ? TransactionsPageV2
      : TransactionsPage,
    external: false,
    requestStates: isBrokerageOrderStatusTablesEnabled()
    ? requestStateConfig['transactionsV2']
    : requestStateConfig['transactions']
  },
  {
    path: '/performance',
    to: '/performance',
    name: 'Performance',
    exact: true,
    component: PerformancePage,
    external: false,
    requestStates: requestStateConfig['performance']
  },
  {
    path: '/taxes',
    to: '/taxes',
    name: 'Taxes',
    exact: true,
    component: TaxesPageV2,
    external: false,
    requestStates: requestStateConfig['taxes']
  },
  {
    path: '/balance-details',
    to: '/balance-details',
    name: 'Balance Details',
    exact: true,
    component: balanceDetailsEnabled ? BalanceDetailsPage : null,
    external: false,
    requestStates: requestStateConfig['balance-details'],
  },
  {
    path: '/statements',
    to: '/statements',
    name: 'Statements',
    exact: true,
    component: StatementsPage,
    external: false,
    requestStates: requestStateConfig['statements']
  },
  {
    path: '/enhanced-personal-services-benefits',
    to: enhancedPersonalServicesBenefitsUrl(),
    name: 'Enhanced Personal Services Benefits',
    exact: true,
    component: oldBenefitsEnabled()
      ? Benefits
      : () => <Redirect to="/program-benefits" />,
    external: !internalEnhancedBenefitsPageEnabled(),
    requestStates: requestStateConfig['enhanced-personal-services-benefits'],
    hide: true
  },
  {
    path: '/personal-services-benefits',
    to: personalServicesBenefitsUrl(),
    name: 'Personal Services Benefits',
    exact: true,
    component: oldBenefitsEnabled()
      ? Benefits
      : () => <Redirect to="/program-benefits" />,
    external: !internalPersonalBenefitsPageEnabled(),
    requestStates: requestStateConfig['personal-services-benefits'],
    hide: true
  },
  {
    path: '/program-benefits',
    to: '/program-benefits',
    name: 'T. Rowe Price Summit Membership Program',
    exact: true,
    component: !oldBenefitsEnabled() ? ProgramBenefitsPage : null,
    external: false,
    requestStates: requestStateConfig['program-benefits'],
    hide: true
  },
  {
    path: '/turbotax-benefits',
    to: '/turbotax-benefits',
    name: 'TurboTax Discounts',
    exact: true,
    component: TurboTaxPage,
    external: false,
    requestStates: requestStateConfig['turbotax-benefits'],
    hide: true
  },
  {
    path: '/client-services-benefits',
    to: coreServicesBenefitsUrl(),
    name: 'Client Services Benefits',
    exact: true,
    component: oldBenefitsEnabled()
      ? Benefits
      : () => <Redirect to="/program-benefits" />,
    external: !internalCoreBenefitsPageEnabled(),
    requestStates: requestStateConfig['client-services-benefits'],
    hide: true
  },
  {
    path: '/brokerage-unavailable',
    to: '/brokerage-unavailable',
    name: 'Brokerage Error Page',
    exact: true,
    component: BrokerageErrorPage,
    external: false,
    requestStates: requestStateConfig['error'],
    hide: true
  },
  {
    path: '/wsj-unavailable',
    to: '/wsj-unavailable',
    name: 'WSJ Error Page',
    exact: true,
    component: WsjErrorPage,
    external: false,
    requestStates: requestStateConfig['error'],
    hide: true
  },
  {
    path: '*/error',
    to: '*/error',
    name: 'Common Error Page',
    exact: true,
    component: ErrorPage,
    external: false,
    requestStates: requestStateConfig['error'],
    hide: true
  },
  {
    path: '/missing-information',
    to: '/missing-information',
    name: 'Missing Information Error Page',
    exact: true,
    component: MissingInfoErrorPage,
    external: false,
    requestStates: requestStateConfig['error'],
    hide: true
  },
  {
    path: '/missing-address',
    to: '/missing-address',
    name: 'Missing Mailing Address Error Page',
    exact: true,
    component: NoAddressErrorPage,
    external: false,
    requestStates: requestStateConfig['error'],
    hide: true
  },
  {
    path: '/excess-accounts',
    to: '/excess-accounts',
    name: 'Excess Accounts (200+ Accounts) Error Page',
    exact: true,
    component: ExcessAccountsPage,
    external: false,
    requestStates: [],
    hide: true
  }
];

export const benefitsRoutes = [
  'enhanced-personal-services',
  'personal-services',
  'client-services',
  'turbotax'
].map((path) => {
  return '/' + path + '-benefits';
});
